'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Map, useMap, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { useProspectSearch } from 'src/context/prospects';
import { useDiscoveryMap } from 'src/context/map';
import { SelectedProspectProvider } from 'src/context/selectedProspects';
import { MedScoutLoadingIcon, AddToListModal, SaveFilterModal, EntityEditDrawer } from 'src/components';
import { useDrawer } from 'src/hooks/drawers/useDrawer';
import { ActiveRouteOverlay } from 'src/components/Routes/layers/ActiveRouteOverlay';
import { SCOUT_DRAWER_ID } from 'src/components/MedScoutAI/constants';
import { useFilterDrawer } from 'src/hooks/drawers/useFilterDrawer';
import { LOCATION } from 'src/hooks/filters/constants';
import { useSelector, useDispatch } from 'react-redux';
import {
  discoveryApi,
  DISCOVERY_LIST_ID,
  DISCOVERY_TAG_TYPE,
} from 'src/api/redux';
import {
  selectDiscoveryMapZoom,
  selectDiscoveryMapCenter,
  selectDiscoveryMapMapBounds,
  selectDiscoveryMapHeatMap,
  selectDiscoveryMapActiveMarker,
  selectDiscoveryMapCurrentTerritory,
  selectDiscoveryMapLastFittedTerritoryId,
  setZoom as setReduxZoom,
  setCenter as setReduxCenter,
  setBounds as setReduxBounds,
  setMapBounds as setReduxMapBounds,
  setActiveMarker as setReduxActiveMarker,
  setResultsListParams as setReduxResultsListParams,
  setLastFittedTerritoryId as setReduxLastFittedTerritoryId,
} from 'src/redux/slices/discoveryMapSlice';
import { useDiscoveryTerritory } from 'src/hooks/discovery';
import { useAppSelector } from 'src/redux/hooks';
import {
  selectPreviewRoute,
  selectRoutesMode,
} from 'src/redux/slices/routesSlice';
import { MAX_WINDOW_WIDTH, MAX_ZOOM } from 'src/components/constants';
import { useWindowSize } from 'src/utils/hooks/useWindowSize';
import { useDebouncedCallback } from 'use-debounce';

import { NO_TERRITORY_ACTIVE_SELECTION } from 'src/components/TerritoryPanel/constants';
import { getPolygonBoundaryCoords } from 'src/utils/territory';
import {
  AllFiltersDrawer,
  ManageFiltersDrawer,
  MedMapContent,
  MedMapMarkers,
  ResultsPanel,
  TerritoryDrawer,
} from './components';

import { ControlsPanel } from './components/ControlsPanel';
import { DiscoveryTerritoryLayer } from './components/DiscoveryTerritoryLayer';
import { AdvancedSearchRadiusLayer } from './components/AdvancedSearchRadiusLayer';
import { useResultsList } from './components/ResultsPanel/context/ResultsListProvider';
import { DiscoveryPanel } from './components/DiscoveryPanel';
import {
  EditCodeSetsDrawer,
  EditEncountersDrawer,
  EncountersDrawer,
  ManageVolumeDrawer,
} from '../Encounters';

const isLoaded = () => {
  return (
    typeof google !== 'undefined' &&
    typeof google.maps !== 'undefined' &&
    typeof google.maps.Map !== 'undefined'
  );
};

const DiscoveryMap = () => {
  const apiIsLoaded = useApiIsLoaded();
  const map = useMap();
  const { width } = useWindowSize();
  const MIN_ZOOM = width > MAX_WINDOW_WIDTH ? 5 : 4;
  const { state: resultsListState, dispatch: resultsListDispatch } =
    useResultsList();

  const dispatch = useDispatch();
  const { showSaveFilterModal, toggleSaveFilterModal } = useFilterDrawer();

  // Initialize territory from user settings
  useDiscoveryTerritory();

  const invalidateDiscoveryListCache = useCallback(() => {
    dispatch(
      discoveryApi.util.invalidateTags([
        { type: DISCOVERY_TAG_TYPE, id: DISCOVERY_LIST_ID },
      ])
    );
  }, [dispatch]);

  const {
    prospectFilters: filters,
    prospectType,
    prospectMode,
  } = useProspectSearch();

  const { selectedTerritories, setSelectTerritories } = useDiscoveryMap();

  const { closeAllButCurrent, openDrawer, isDrawerOpen } = useDrawer();

  const [newProviders, setNewProviders] = useState(null);
  const [mapApiIsLoading, setMapApiIsLoading] = React.useState(true);

  const [onAddSuccessCallback, setOnAddSuccessCallback] = useState<
    (() => void) | null
  >(null);

  const mapContainerRef = React.useRef<HTMLDivElement>(null);

  const isMapReady = useRef(false);

  useEffect(() => {
    isMapReady.current = apiIsLoaded;
  }, [apiIsLoaded]);

  useEffect(() => {
    if (!apiIsLoaded) return;
    setMapApiIsLoading(false);
  }, [apiIsLoaded, map]);

  // Read from Redux
  const heatMap = useSelector(selectDiscoveryMapHeatMap);
  const zoom = useSelector(selectDiscoveryMapZoom);
  const center = useSelector(selectDiscoveryMapCenter);
  const mapBounds = useSelector(selectDiscoveryMapMapBounds);
  const activeMarker = useSelector(selectDiscoveryMapActiveMarker);
  const currentTerritory = useSelector(selectDiscoveryMapCurrentTerritory);
  const lastFittedTerritoryId = useSelector(
    selectDiscoveryMapLastFittedTerritoryId
  );

  // Fallback: read bounds directly from the map instance once it's ready.
  // The React onBoundsChanged handler can miss the initial bounds_changed event
  // due to timing (isMapReady not yet set, debounce, etc.). Try reading bounds
  // immediately (covers the case where idle already fired), and register an
  // idle listener as a fallback for when the map hasn't finished rendering yet.
  useEffect(() => {
    if (!map || !apiIsLoaded || mapBounds) return;

    const dispatchBounds = () => {
      const bounds = map.getBounds();
      if (!bounds) return false;

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const north = ne.lat();
      const south = sw.lat();
      const east = ne.lng();
      const west = sw.lng();

      if (north === 0 && south === 0 && east === 0 && west === 0) return false;

      dispatch(
        setReduxBounds([
          [north, west],
          [south, east],
        ] as any)
      );
      dispatch(
        setReduxMapBounds([
          [west, north],
          [east, north],
          [east, south],
          [west, south],
          [west, north],
        ] as any)
      );
      dispatch(setReduxZoom(map.getZoom() || MIN_ZOOM));
      return true;
    };

    // Try immediately — idle may have already fired
    if (dispatchBounds()) return;

    // Otherwise wait for the next idle event
    const listener = google.maps.event.addListenerOnce(map, 'idle', () => {
      dispatchBounds();
    });

    return () => {
      listener.remove();
    };
  }, [map, apiIsLoaded, mapBounds, dispatch, MIN_ZOOM]);

  // Fit map bounds to territory only when the territory actually changes (different ID)
  useEffect(() => {
    if (!map || !currentTerritory || !isLoaded()) return;

    // Skip if we already fitted to this territory
    if (lastFittedTerritoryId === currentTerritory.id) return;

    const isNew = !!currentTerritory?.polygons;
    const paths = isNew
      ? currentTerritory?.polygons
      : currentTerritory?.old_polygons;

    if (!paths) return;

    const bounds = new google.maps.LatLngBounds();
    paths.forEach((polygon) => {
      const boundaryCoords = getPolygonBoundaryCoords(polygon);
      boundaryCoords.forEach((point) => {
        if (
          point &&
          typeof point.lat === 'number' &&
          typeof point.lng === 'number'
        ) {
          bounds.extend(new google.maps.LatLng(point.lat, point.lng));
        }
      });
    });

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds);
      map.setCenter(bounds.getCenter());
    }

    dispatch(setReduxLastFittedTerritoryId(currentTerritory.id));
  }, [map, currentTerritory, lastFittedTerritoryId, dispatch]);

  useEffect(() => {
    // Always open the results drawer when component loads.
    // If scout-ai is open (e.g. "View on Map"), skip closeAllButCurrent
    // so the AI drawer stays open alongside results.
    if (isDrawerOpen(SCOUT_DRAWER_ID)) {
      openDrawer('resultsDrawer');
    } else {
      openDrawer('resultsDrawer');
      closeAllButCurrent('resultsDrawer');
    }
  }, []);

  useEffect(() => {
    if (!currentTerritory) {
      setSelectTerritories([]);
    }

    // TODO: This is temporary until we remove the old map
    if (currentTerritory?.id) {
      // check if currentTerritory is in selectedTerritories
      // don't add it if it is
      const isAlreadySelected = selectedTerritories.some(
        (territory) => territory.id === currentTerritory.id
      );

      if (!isAlreadySelected) {
        setSelectTerritories([currentTerritory]);
      }
    }
  }, [currentTerritory]);

  useEffect(() => {
    let containsNonExclusionPaymentsFilter = false;

    const groupedFiltersByContext = filters.reduce(
      (result, { context, value }) => {
        if (!containsNonExclusionPaymentsFilter) {
          // [id, min, max] value pattern for payments, when max is defined as 0 this is an 'exclusion payment filter'
          containsNonExclusionPaymentsFilter =
            context !== 'payments' ? true : (value?.['2'] ?? null) !== 0;
        }

        if (!result[context]) {
          result[context] = context === 'year' ? value : [value];
        } else {
          result[context] = [...result[context], value];
        }

        return result;
      },
      {}
    );

    const isEmpty = Object.keys(groupedFiltersByContext)?.length === 0;
    const sortedParams = !isEmpty
      ? Object.keys(groupedFiltersByContext).reduce((result, key) => {
          const currentParams = groupedFiltersByContext[key];
          if (Array.isArray(currentParams)) {
            result[key] = Array.from(new Set(currentParams)).sort();
          } else {
            result[key] = currentParams;
          }
          return result;
        }, {})
      : {};

    const selectedTerritoryIds = selectedTerritories.reduce(
      (result, territory) => {
        if (territory?.id?.toString() !== NO_TERRITORY_ACTIVE_SELECTION.id) {
          result.push(territory?.id);
        }
        return result;
      },
      []
    );

    const data = {
      type: prospectType,
      mode: prospectMode,
      ex_pr: 0,
      map: selectedTerritoryIds ?? [],
      zoom: zoom || 4,
      filters: sortedParams,
    };

    dispatch(
      setReduxResultsListParams({
        data,
        enabled: true,
      })
    );
  }, [prospectType, prospectMode, filters, selectedTerritories, zoom]);

  const handleBoundsChanged = useCallback(
    (bounds: any) => {
      if (!bounds || !apiIsLoaded) return;

      if (!isMapReady.current || !bounds?.detail?.bounds) return;

      const { north, south, east, west } = bounds.detail.bounds;
      if (north == null || south == null || east == null || west == null) return;

      dispatch(
        setReduxBounds([
          [north, west],
          [south, east],
        ])
      );

      dispatch(
        setReduxMapBounds([
          [west, north],
          [east, north],
          [east, south],
          [west, south],
          [west, north],
        ])
      );

      const newZoom = bounds.detail.zoom || MIN_ZOOM;
      dispatch(setReduxZoom(newZoom));
      if (bounds.detail.center) {
        dispatch(
          setReduxCenter({
            lat: bounds.detail.center.lat,
            lng: bounds.detail.center.lng,
          })
        );
      }
    },
    [apiIsLoaded, dispatch]
  );

  const debouncedBoundsChanged = useDebouncedCallback(handleBoundsChanged, 500);

  const handleZoomChanged = (zoom: any) => {
    if (!zoom || !apiIsLoaded) return;
    const newZoom = zoom?.detail?.zoom || MIN_ZOOM;
    dispatch(setReduxZoom(newZoom));
  };

  const addToList = (providersArray: any[], onSuccess: () => void) => {
    if (!providersArray || providersArray.length === 0) return;
    setNewProviders(structuredClone(providersArray));
    setOnAddSuccessCallback(() => onSuccess);
    resultsListDispatch({
      type: 'SET_SHOW_ADD_TO_LISTS_DIALOG',
      payload: true,
    });
  };

  const handleCloseAddToListModal = () => {
    resultsListDispatch({
      type: 'SET_SHOW_ADD_TO_LISTS_DIALOG',
      payload: false,
    });
    setNewProviders(null);
    setOnAddSuccessCallback(null);
  };

  const handleAddToListSuccess = () => {
    // Trigger callback if provided (from map marker tooltip)
    if (onAddSuccessCallback) {
      onAddSuccessCallback();
    } else {
      // Clear selected providers from results panel
      resultsListDispatch({
        type: 'SET_SELECTED_PROVIDERS',
        payload: [],
      });
    }

    // Invalidate Discovery list cache so results show updated list membership
    invalidateDiscoveryListCache();

    handleCloseAddToListModal();
  };

  // Get providers for the add to list modal
  // Use newProviders (from map marker tooltip) or fall back to context selectedProviders (from results panel)
  const addToListProviders = newProviders || resultsListState.selectedProviders || [];

  // A route previewed from Scout is the focus, so search markers step aside. A
  // route open in the drawer keeps them: markers are how stops get added.
  const previewRoute = useAppSelector(selectPreviewRoute);
  const routesMode = useAppSelector(selectRoutesMode);
  const isPreviewingRoute = !!previewRoute && routesMode === 'idle';

  const showMarkers = !heatMap && !isPreviewingRoute;

  // Add an effect to attach wheel event listener
  // this is specifically for trackpad zooming
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) {
        e.preventDefault();
      }
    };

    const mapContainer = mapContainerRef.current;
    if (mapContainer) {
      mapContainer.addEventListener('wheel', handleWheel, {
        passive: false,
      } as AddEventListenerOptions);
    }

    return () => {
      if (mapContainer) {
        mapContainer.removeEventListener('wheel', handleWheel, {
          passive: false,
        } as EventListenerOptions);
      }
    };
  }, []);

  if (!isLoaded() || mapApiIsLoading || !apiIsLoaded) {
    return (
      <div className="flex justify-center items-center w-full h-full relative">
        <MedScoutLoadingIcon />
      </div>
    );
  }

  return (
    <SelectedProspectProvider>
      <div className="flex w-full h-full relative overflow-hidden">
        <div className="@container relative w-full h-full" ref={mapContainerRef}>
          <Map
            mapId="discovery-map"
            draggableCursor="pointer"
            draggingCursor="move"
            className="w-full h-full"
            defaultCenter={center}
            defaultZoom={MIN_ZOOM}
            zoom={zoom}
            gestureHandling={'greedy'}
            disableDefaultUI
            onBoundsChanged={debouncedBoundsChanged}
            onZoomChanged={handleZoomChanged}
            maxZoom={MAX_ZOOM}
            minZoom={MIN_ZOOM}
            scrollwheel
            zoomControl={false}
            zoomControlOptions={undefined}
          >
            <MedMapMarkers
              activeMarker={activeMarker}
              setActiveMarker={(marker) => dispatch(setReduxActiveMarker(marker))}
              addToList={addToList}
              showMarkers={showMarkers}
            />
            <DiscoveryTerritoryLayer />
            <AdvancedSearchRadiusLayer />
            <MedMapContent />
            <ActiveRouteOverlay showPreview />
            <ControlsPanel />
          </Map>
          {/* Add Edit Panel */}
          <DiscoveryPanel />
        </div>
        <ResultsPanel />
        {map && mapBounds && (
          <>
            <AllFiltersDrawer
              hideBackdrop={true}
              anchor="right"
              size="base2"
            />
            <ManageFiltersDrawer
              hideBackdrop={true}
              anchor="right"
              size={'base2'}
            />
            <EntityEditDrawer location="discovery" />
            <EncountersDrawer />
            <EditEncountersDrawer />
            <EditCodeSetsDrawer />
            <ManageVolumeDrawer location={LOCATION.DISCOVERY} />
            <TerritoryDrawer />
          </>
        )}
      </div>
      <AddToListModal
        isOpen={resultsListState.showAddToListsDialog}
        onClose={handleCloseAddToListModal}
        mode="add"
        providers={addToListProviders}
        onSuccess={handleAddToListSuccess}
      />
      <SaveFilterModal
        open={showSaveFilterModal}
        onClose={toggleSaveFilterModal}
        location={LOCATION.DISCOVERY}
      />
    </SelectedProspectProvider>
  );
};

export default DiscoveryMap;
